import { Component, OnInit, OnDestroy, ElementRef, HostListener } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Title } from '@angular/platform-browser';
import { AuthService } from '../services/auth.service';
import { Observable, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, CommonModule],
  template: `
    <!-- Chevron NextPage: top identity bar -->
    <div class="cvx-topbar">
      <div class="cvx-topbar-inner">
        <!-- Chevron logo -->
        <div class="cvx-brand">
          <img src="assets/chevron-logo.png" alt="Chevron" class="cvx-logo" height="44" width="auto" />
          <span class="cvx-divider">|</span>
          <span class="cvx-app-name">NEST<span *ngIf="envLabel" class="cvx-env-badge cvx-env-badge--{{ envLabel }}">{{ envLabel }}</span></span>
        </div>

        <!-- User welcome (center-right) -->
        <div class="cvx-meta">
          <span class="cvx-user-welcome">Welcome, {{ currentUser$ | async }}</span>
        </div>
      </div>
    </div>

    <!-- Chevron NextPage: navigation bar -->
    <nav class="cvx-navbar">
      <div class="cvx-navbar-inner">
        <!-- NORMs group: General lookup + estimators (all are NORMs-based) -->
        <a *ngIf="!hasNormsMenu" routerLink="/norms-lookup" routerLinkActive="active" class="cvx-navlink">NORMs Lookup</a>
        <div *ngIf="hasNormsMenu" class="cvx-navgroup">
          <button type="button" class="cvx-navlink cvx-navbtn" [class.active]="normsActive"
                  aria-haspopup="menu" [attr.aria-expanded]="normsOpen" aria-controls="norms-menu"
                  (click)="toggleNorms()" (keydown)="onButtonKeydown($event)">
            NORMs Lookup
            <span class="cvx-caret" [class.open]="normsOpen" aria-hidden="true"></span>
          </button>
          <div *ngIf="normsOpen" id="norms-menu" class="cvx-menu" role="menu" (keydown)="onMenuKeydown($event)">
            <a role="menuitem" routerLink="/norms-lookup" routerLinkActive="active" class="cvx-menuitem">General NORMs Lookup</a>
            <a *ngIf="showPipeFab" role="menuitem" routerLink="/pipe-fab" routerLinkActive="active" class="cvx-menuitem">Pipe Fabrication Estimator</a>
            <a *ngIf="showSoftCraft" role="menuitem" routerLink="/soft-craft" routerLinkActive="active" class="cvx-menuitem">Soft Craft Estimator</a>
          </div>
        </div>
        <a routerLink="/labor-hours"  routerLinkActive="active" class="cvx-navlink">Historical Durations</a>
        <a routerLink="/dashboard"    routerLinkActive="active" class="cvx-navlink">Dashboard</a>
      </div>
    </nav>
  `,
  styles: [`
    /* ── Host: sticky so entire header (topbar + nav) pins together ── */
    :host {
      display: block;
      position: sticky;
      top: 0;
      z-index: 200;
    }

    /* ── Chevron NextPage Top Identity Bar ── */
    .cvx-topbar {
      background: #ffffff;
      color: var(--cvx-blue);
      box-shadow: 0 1px 3px rgba(0,0,0,0.15); border-bottom: 1px solid #e0e7ef;
    }

    .cvx-topbar-inner {
      display: flex;
      align-items: center;
      justify-content: space-between;
      max-width: 1360px;
      margin: 0 auto;
      padding: 0 var(--spacing-lg);
      height: 64px;
    }

    .cvx-brand {
      display: flex;
      align-items: center;
      gap: 0.625rem;
    }

    .cvx-logo {
      height: 44px;
      width: auto;
      flex-shrink: 0;
      display: block;
    }

    .cvx-divider {
      color: rgba(0,48,135,0.25);
      font-size: 1.125rem;
      line-height: 1;
    }

    .cvx-app-name {
      font-size: 0.9375rem;
      font-weight: 700;
      letter-spacing: 0.08em;
      color: var(--cvx-blue);
      display: flex;
      align-items: center;
      gap: 0.375rem;
    }

    .cvx-env-badge {
      font-size: 0.6rem;
      font-weight: 700;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      padding: 0.15rem 0.45rem;
      border-radius: 3px;
      vertical-align: middle;
      position: relative;
      top: -1px;
      line-height: 1.4;
    }

    .cvx-env-badge--dev {
      color: #92400e;
      background: #fef3c7;
      border: 1px solid #f59e0b;
    }

    .cvx-env-badge--test {
      color: #1e3a8a;
      background: #dbeafe;
      border: 1px solid #3b82f6;
    }

    .cvx-meta {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .cvx-user-welcome {
      font-size: 0.8125rem;
      font-weight: 500;
      color: var(--cvx-blue);
      letter-spacing: 0.02em;
      min-width: 8rem;   /* reserve space so auth load doesn't reflow header */
      display: inline-block;
    }

    .cvx-version {
      font-size: 0.6875rem;
      font-weight: 600;
      color: var(--cvx-blue);
      background: rgba(0,48,135,0.08);
      padding: 0.1875rem 0.625rem;
      border-radius: 2px;
      letter-spacing: 0.04em;
    }

    .cvx-env {
      font-size: 0.6875rem;
      font-weight: 700;
      color: var(--cvx-red);
      background: rgba(200,16,46,0.07);
      padding: 0.1875rem 0.625rem;
      border-radius: 2px;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      border: 1px solid rgba(200,16,46,0.25);
    }

    /* ── Chevron NextPage Navigation Bar ── */
    .cvx-navbar {
      background: var(--light-gray);
    }

    .cvx-navbar-inner {
      display: flex;
      align-items: stretch;
      max-width: 1360px;
      margin: 0 auto;
      padding: 0 var(--spacing-lg);
      height: 42px;
      background: #002370;
      border-bottom: 3px solid var(--cvx-mid);
    }

    .cvx-navlink {
      display: flex;
      align-items: center;
      padding: 0 1.125rem;
      color: rgba(255,255,255,0.72);
      text-decoration: none;
      font-size: 0.8125rem;
      font-weight: 500;
      border-bottom: 3px solid transparent;
      margin-bottom: -3px;
      transition: color 0.15s ease, border-color 0.15s ease;
      white-space: nowrap;
    }

    .cvx-navlink:hover {
      color: #ffffff;
      border-bottom-color: rgba(255,255,255,0.4);
    }

    .cvx-navlink.active {
      color: #ffffff;
      border-bottom-color: #ffffff;
      font-weight: 600;
    }

    /* ── NORMs dropdown ── */
    .cvx-navgroup {
      position: relative;
      display: flex;
      align-items: stretch;
    }

    .cvx-navbtn {
      background: none;
      border: none;
      border-bottom: 3px solid transparent;
      font-family: inherit;
      cursor: pointer;
      gap: 0.45rem;
    }

    .cvx-navbtn:focus-visible,
    .cvx-menuitem:focus-visible {
      outline: 2px solid #ffffff;
      outline-offset: -2px;
    }

    .cvx-caret {
      width: 0.4rem;
      height: 0.4rem;
      border-right: 2px solid currentColor;
      border-bottom: 2px solid currentColor;
      transform: rotate(45deg) translateY(-2px);
      transition: transform 0.15s ease;
    }

    .cvx-caret.open {
      transform: rotate(225deg) translateY(-2px);
    }

    .cvx-menu {
      position: absolute;
      top: 100%;
      left: 0;
      min-width: 16rem;
      margin-top: 3px;
      background: #ffffff;
      border: 1px solid #e0e7ef;
      border-top: 3px solid var(--cvx-mid);
      box-shadow: 0 6px 16px rgba(0,0,0,0.18);
      display: flex;
      flex-direction: column;
      z-index: 210;
    }

    .cvx-menuitem {
      padding: 0.65rem 1rem;
      color: var(--cvx-blue);
      text-decoration: none;
      font-size: 0.8125rem;
      font-weight: 500;
      white-space: nowrap;
      border-left: 3px solid transparent;
    }

    .cvx-menuitem:hover,
    .cvx-menuitem:focus {
      background: var(--cvx-light);
    }

    .cvx-menuitem.active {
      font-weight: 700;
      border-left-color: var(--cvx-mid);
    }
  `]
})
export class HeaderComponent implements OnInit, OnDestroy {
  currentUser$: Observable<string>;
  appVersion = '1.0.0';
  buildTimestamp = '';
  envLabel = environment.envLabel ?? '';
  showPipeFab = !!(environment as { showPipeFab?: boolean }).showPipeFab;
  showSoftCraft = !!(environment as { showSoftCraft?: boolean }).showSoftCraft;
  normsOpen = false;
  private destroy$ = new Subject<void>();

  constructor(
    private authService: AuthService,
    private titleService: Title,
    private router: Router,
    private host: ElementRef<HTMLElement>
  ) {
    this.currentUser$ = this.authService.getCurrentUser$().pipe(
      map(user => user?.displayName || 'User')
    );
  }

  get hasNormsMenu(): boolean {
    return this.showPipeFab || this.showSoftCraft;
  }

  get normsActive(): boolean {
    const path = this.router.url.split(/[?#]/)[0];
    return ['/norms-lookup', '/pipe-fab', '/soft-craft'].includes(path);
  }

  toggleNorms(): void {
    this.normsOpen = !this.normsOpen;
  }

  onButtonKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.normsOpen = true;
      setTimeout(() => this.menuItems()[0]?.focus());
    }
  }

  onMenuKeydown(event: KeyboardEvent): void {
    const items = this.menuItems();
    const i = items.indexOf(document.activeElement as HTMLElement);
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      items[(i + 1) % items.length]?.focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      items[(i - 1 + items.length) % items.length]?.focus();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.normsOpen && !this.host.nativeElement.querySelector('.cvx-navgroup')?.contains(event.target as Node)) {
      this.normsOpen = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (!this.normsOpen) return;
    this.normsOpen = false;
    this.host.nativeElement.querySelector<HTMLElement>('.cvx-navbtn')?.focus();
  }

  private menuItems(): HTMLElement[] {
    return Array.from(this.host.nativeElement.querySelectorAll<HTMLElement>('.cvx-menuitem'));
  }

  ngOnInit(): void {
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe(() => { this.normsOpen = false; });

    this.titleService.setTitle(this.envLabel ? `NEST NORMs \u2013 ${this.envLabel}` : 'NEST NORMs');

    const now = new Date();
    this.buildTimestamp = now.toISOString().split('T')[0] + ' ' + now.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit',
      hour12: true 
    });

    // currentUser$ is bound via async pipe in the template
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
